import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';

// The single owner of the notification read/dismiss lifecycle on the
// presentation side: deterministic toast identity, retracting toasts when
// state changes, and the mark-read/dismiss API commands. The bell and the
// organization-events hook delegate here instead of each re-implementing
// toast + API details.

export type NotificationStateUpdate = {
    readonly notificationId: string;
    readonly isRead: boolean;
    readonly isDismissed: boolean;
};

// One deterministic sonner toast id per (organization, notification), so
// read/dismiss state changes anywhere (bell, another tab, the extension's
// browser pop, the hygiene sweep) can retract a still-visible toast for
// the same notification. Also dedupes redelivered notifications into a
// single toast instead of stacking copies.
export function buildNotificationToastId(
    organizationId: string,
    notificationId: string,
): string {
    return `organization-event:${organizationId}:${notificationId}`;
}

export function dismissNotificationToast(
    organizationId: string,
    notificationId: string,
): void {
    toast.dismiss(buildNotificationToastId(organizationId, notificationId));
}

// Retract toasts for any update that marks a notification read or
// dismissed, wherever that state change originated.
export function retractToastsForStateUpdates(
    organizationId: string,
    updates: ReadonlyArray<NotificationStateUpdate>,
): void {
    for (const update of updates) {
        if (update.isRead || update.isDismissed) {
            dismissNotificationToast(organizationId, update.notificationId);
        }
    }
}

// Mark notifications read: retracts their toasts immediately, then persists.
// The server's notification_state_updated fan-out syncs every other surface
// and tab. Rejects on API failure so callers choose their own recovery.
export async function markNotificationsRead(
    organizationId: string,
    notificationIds: ReadonlyArray<string>,
    reason: string,
): Promise<void> {
    if (notificationIds.length === 0) {
        return;
    }
    for (const notificationId of notificationIds) {
        dismissNotificationToast(organizationId, notificationId);
    }
    await getApiClient().post({
        url: '/organizations/{organization_id}/notifications/mark-read',
        pathParams: { organization_id: organizationId },
        body: { notification_ids: [...notificationIds] },
        reason,
    });
}

// Dismiss notifications (implies read server-side); same toast + fan-out
// semantics as markNotificationsRead.
export async function dismissNotifications(
    organizationId: string,
    notificationIds: ReadonlyArray<string>,
    reason: string,
): Promise<void> {
    if (notificationIds.length === 0) {
        return;
    }
    for (const notificationId of notificationIds) {
        dismissNotificationToast(organizationId, notificationId);
    }
    await getApiClient().post({
        url: '/organizations/{organization_id}/notifications/dismiss',
        pathParams: { organization_id: organizationId },
        body: { notification_ids: [...notificationIds] },
        reason,
    });
}
